// Administración · Enlaces compartidos (T-zhjj, handoff §9, plan §2.5):
// la pantalla de facility bajo /administracion/enlaces. La tabla del
// prototipo —Enlace · Contenido · Para · Vence · Accesos · acciones— sobre
// los enlaces que /share-bundles entrega hoy; routes/EnlacesCompartidos.tsx
// sigue sirviendo a postventa sin cambios.
//
// Lo que el handoff pide y el backend aún no da queda oculto, no
// simulado: «Copiar» (la lista no trae la URL; se copia al crear) y el
// nombre interno. La columna Accesos es el `accessCount` de la lista, en
// mono; el detalle se abre por fila —«Ver registro», en las acciones— con
// GET /share-bundles/:id/accesses. Revocar es inmediato, como dice el
// handoff, con el toast de la casa.

import { Fragment, useState } from "react";
import { isReadOnlyRole } from "@bitacorapro/api-types";
import { EmptyState } from "../../components/EmptyState";
import { Icon } from "../../components/icons";
import { PageHeader } from "../../components/PageHeader";
import { Panel } from "../../components/Panel";
import { QueryBoundary } from "../../components/QueryBoundary";
import { Skel } from "../../components/Skel";
import { SkelTable } from "../../components/skeleton/SkelTable";
import { CrearEnlaceDrawer } from "../../features/administracion/enlaces/CrearEnlaceDrawer";
import {
  accesoAccionLabel,
  contenidoLabel,
  enlaceLabel,
} from "../../features/administracion/enlaces/model";
import { useEnlaces, useRevocarEnlace } from "../../features/administracion/enlaces/queries";
import { adminWords } from "../../lib/admin/words";
import { useRecipe, useShareBundleAccesses } from "../../lib/api/queries";
import { useSession } from "../../lib/auth/useSession";
import { canManageShareBundles } from "../../lib/capabilities";
import { ADMIN_TOAST_OPTS, useToast } from "../../lib/useToast";
import { fmtDate, fmtDateTime } from "../../lib/util";

// Cabeceras fijas para que el esqueleto pinte la misma fila que los datos.
const HEADERS = ["Enlace", "Contenido", "Para", "Vence", "Accesos", ""];

export function Enlaces() {
  const { data: recipe } = useRecipe();
  const session = useSession();
  const role = session?.organization.role;
  // Quien administra la organización (admin, platformAdmin o el rol
  // `orgAdmin` de la receta: el gerente de facility) crea, revoca y lee
  // el registro; es el espejo de requireOrgAdmin en core-api. Los roles
  // de solo lectura (ADR-0164) nunca ven crear ni revocar, admita lo que
  // admita canManageShareBundles: core-api responde 403 ROLE_READ_ONLY.
  const canManage =
    canManageShareBundles(recipe, role, { platformAdmin: session?.user.platformAdmin === true }) &&
    !isReadOnlyRole(role ?? "");

  const enlaces = useEnlaces();
  const revocar = useRevocarEnlace();
  const toast = useToast();
  const [creando, setCreando] = useState(false);
  const [abierto, setAbierto] = useState<string | null>(null);

  const words = adminWords(recipe);
  const finding = recipe?.labels.finding ?? "Observación";
  const findingPlural = recipe?.labels.findingPlural ?? "Observaciones";

  function onRevocar(id: string) {
    revocar.mutate(id, {
      onSuccess: () => {
        toast.show("Enlace revocado · deja de abrir de inmediato", "ok", ADMIN_TOAST_OPTS);
        if (abierto === id) setAbierto(null);
      },
      onError: () => {
        toast.show("No pudimos revocar el enlace. Intenta de nuevo.", "err", ADMIN_TOAST_OPTS);
      },
    });
  }

  return (
    <div className="page">
      <PageHeader
        eyebrow={words.module}
        title={words.sections.enlaces}
        sub="Paquetes de actas u observaciones para personas fuera de BitácoraPro. Puedes revocarlos en cualquier momento."
        actions={
          canManage && (
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => setCreando(true)}
              title="Abre el panel para crear un enlace compartido"
            >
              <Icon.Plus s={14} /> Crear enlace
            </button>
          )
        }
      />

      <QueryBoundary
        query={enlaces}
        label="los enlaces compartidos"
        skeleton={
          <Panel flush>
            <SkelTable rows={6} cols={HEADERS.length} headers={HEADERS} />
          </Panel>
        }
        isEmpty={(data) => data.bundles.length === 0}
        empty={
          <Panel flush>
            <EmptyState
              title="Sin enlaces"
              description={`Crea un enlace para compartir ${findingPlural.toLowerCase()} con personas fuera de BitácoraPro.`}
            />
          </Panel>
        }
      >
        {(data) => (
          <Panel flush>
            <table className="dtable">
              <thead>
                <tr>
                  {HEADERS.map((h, i) => (
                    <th key={i}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.bundles.map((b) => {
                  const expandido = abierto === b.id;
                  const revocando = revocar.isPending && revocar.variables === b.id;
                  return (
                    <Fragment key={b.id}>
                      <tr>
                        <td>
                          <span className="cell-strong">{enlaceLabel(b, findingPlural)}</span>
                        </td>
                        <td>{contenidoLabel(b.itemCount, finding, findingPlural)}</td>
                        <td>
                          {b.recipientLabel ? (
                            b.recipientLabel
                          ) : (
                            <span className="cell-meta">—</span>
                          )}
                        </td>
                        <td>
                          <span className="cell-meta">
                            {b.expiresAt ? fmtDate(b.expiresAt) : "Sin vencimiento"}
                          </span>
                        </td>
                        <td>
                          <span className="mono">{b.accessCount}</span>
                        </td>
                        <td className="cell-actions">
                          {canManage && (
                            <>
                              <button
                                type="button"
                                className="btn btn--ghost btn--sm"
                                aria-expanded={expandido}
                                onClick={() => setAbierto(expandido ? null : b.id)}
                                title={
                                  expandido
                                    ? "Oculta el registro de accesos de este enlace"
                                    : "Muestra quién abrió este enlace y cuándo"
                                }
                              >
                                {expandido ? "Ocultar registro" : "Ver registro"}
                              </button>
                              <button
                                type="button"
                                className="btn btn--danger btn--sm"
                                disabled={revocando}
                                onClick={() => onRevocar(b.id)}
                                title="Revoca este enlace: deja de abrir de inmediato"
                              >
                                Revocar
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                      {canManage && expandido && (
                        <tr>
                          <td colSpan={HEADERS.length}>
                            <RegistroAccesos bundleId={b.id} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </Panel>
        )}
      </QueryBoundary>

      {creando && <CrearEnlaceDrawer onClose={() => setCreando(false)} />}
    </div>
  );
}

// El registro de accesos de un enlace, como fila desplegada. Sección
// dentro de una tabla ya cargada: sin anuncio propio y un error que no
// mueve el foco. Muestra fecha y acción; la IP y el navegador que guarda
// `share_link_accesses` son datos personales de un tercero y se quedan en
// el servidor (Ley 21.719, minimización).
function RegistroAccesos({ bundleId }: { bundleId: string }) {
  const accesos = useShareBundleAccesses(bundleId);
  return (
    <QueryBoundary
      query={accesos}
      label="el registro de accesos"
      kind="section"
      skeleton={
        <div className="ag-sm">
          <Skel variant="text" width="30%" />
          <Skel variant="text" width="45%" />
        </div>
      }
      isEmpty={(data) => data.accesses.length === 0}
      empty={<p className="ag-muted">Nadie ha abierto este enlace todavía.</p>}
    >
      {(data) => (
        <div className="ag-sm">
          <p className="ag-strong ag-mb8">
            <span className="mono">{data.accesses.length}</span>{" "}
            {data.accesses.length === 1 ? "acceso" : "accesos"}
          </p>
          {/* `.ag-accesos` (lista sin viñeta ni margen) la define admin.css,
              fuera de esta pieza; el style inline se va cuando exista. */}
          <ul
            className="ag-muted ag-accesos"
            style={{ margin: 0, paddingLeft: 0, listStyle: "none" }}
          >
            {data.accesses.map((a, i) => (
              <li key={`${a.accessedAt}-${i}`}>
                <span className="mono">{fmtDateTime(a.accessedAt)}</span>
                {" · "}
                {accesoAccionLabel(a.action)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </QueryBoundary>
  );
}

export default Enlaces;
